import React, { useEffect, useState } from 'react';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { ContentContainer } from '../ContentContainer';
import { Box, Button, Typography } from '@mui/material';
import { DeviceMasterProvider, useDeviceMaster } from '../../data/DataProviders/DeviceMasterProvider';
import { Add } from '@mui/icons-material';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { pushAppRoute } from '../../data/domain/route-manager';
import { useHistory } from 'react-router';
import { GenericToolbar } from '../GenericToolbar';
import { BLUEPRINT_ORGANIZATION_NAME } from '../../constants';
import { isDataLoaded } from '../../types';
import { sortObjectArrayNaturally } from '../../lib';
import { DeviceProfileData } from '../../constants/manage-device-profile-data';
import { DeviceTypeListGroups } from './DeviceProfileListGroups';
import { DeviceProfileListItem } from './DeviceProfileListItem';
import { DataIndicators } from '../DataIndicators';
import { useSelector } from 'react-redux';
import { Selectors } from '../../selectors';
import { ChildProfileDetailsProvider } from '../../data/DataProviders/ChildProfileDetailsProvider';
import { isRecordFound, useSelectedOrganizationData } from '../../hooks/data-hooks';
import { sortList } from '../../lib/manage-alarms-helpers';
import { MasterDataKey } from '../../types/device';

export const ManageDeviceProfiles: React.FC = () => {
    const { t } = useLanguageLocale();
    const currentAppRoute = useCurrentAppRoute();
    const history = useHistory();
    const deviceMasterData = useDeviceMaster();
    const [deviceProfileList, setDeviceProfileList] = useState<DeviceProfileData[]>([]);
    const [orgDeviceProfileList, setOrgDeviceProfileList] = useState<DeviceProfileData[]>([]);
    const selectedDeviceProfile = useSelector(Selectors.ManageDeviceProfileSelectors.selectedDeviceProfile);
    const selectedOrganization = useSelectedOrganizationData();
    const orgId = isRecordFound(selectedOrganization) ? selectedOrganization.value.id : '';

    useEffect(() => {
        deviceMasterData.getDevicesInfo(BLUEPRINT_ORGANIZATION_NAME, MasterDataKey.Starfish);
    }, []);

    useEffect(() => {
        if (
            isDataLoaded(deviceMasterData.deviceInfo) &&
            deviceMasterData.deviceInfo.value &&
            deviceMasterData.deviceInfo.value.length > 0 &&
            deviceMasterData.rowKey === MasterDataKey.Starfish
        ) {
            deviceMasterData.getDevicesInfo(BLUEPRINT_ORGANIZATION_NAME, orgId);
            const childDeviceInfo = deviceMasterData.deviceInfo.value;
            if (childDeviceInfo) {
                setDeviceProfileList(childDeviceInfo);
            }
        }
    }, [deviceMasterData]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (
            isDataLoaded(deviceMasterData.deviceInfo) &&
            deviceMasterData.deviceInfo.value &&
            deviceMasterData.deviceInfo.value.length > 0 &&
            deviceMasterData.rowKey === orgId
        ) {
            const childDeviceInfo = deviceMasterData.deviceInfo.value;
            if (childDeviceInfo) {
                const orgProfiles = JSON.parse(JSON.stringify(childDeviceInfo));
                sortList(orgProfiles, 'key');
                setOrgDeviceProfileList(orgProfiles);
            }
        }
    }, [deviceMasterData]); //eslint-disable-line react-hooks/exhaustive-deps

    const deviceProfileData: DeviceProfileData[] = deviceProfileList
        ? sortObjectArrayNaturally(deviceProfileList, [{ sortField: 'key' }])
        : [];

    const deviceProfileGrp = deviceProfileData.map((item) => ({
        key: item.key,
        profileId: item.value.profileId,
        profileName: item.value.profileName,
    }));
    const navigateToAddDeviceProfile = (): void => {
        // dispatch(StarfishDeviceActions.StarfishChildDeviceRegistrationUnmounted());
        if (currentAppRoute.type === 'ManageDeviceProfiles')
            pushAppRoute(history, {
                type: 'AddManageDeviceProfile',
                orgId: currentAppRoute.orgId,
            });
    };
    return (
        <>
            <GenericToolbar title={t('MANAGEDEVICEPROFILES.TITLE')} subtitle={t('MANAGEDEVICEPROFILES.SUBTITLE')} />

            <DeviceMasterProvider>
                <ContentContainer>
                    <Box display="flex" justifyContent="space-between">
                        <Box display="block" justifyContent="flex-start">
                            <Typography
                                color="primary"
                                variant="subtitle1"
                                display="block"
                                style={{ lineHeight: 1.5, fontWeight: 600 }}
                            >
                                Device Profiles
                            </Typography>

                            <Typography variant="body2" display="flex" style={{ lineHeight: 1.25, fontWeight: 400 }}>
                                {deviceProfileData.length} Device Profiles
                                {orgDeviceProfileList.length > 0 && (
                                    <Typography
                                        variant="body2"
                                        display="flex"
                                        style={{ lineHeight: 1.25, fontWeight: 400 }}
                                    >
                                      |{orgDeviceProfileList.length} Derived Profiles
                                    </Typography>
                                )}
                            </Typography>
                        </Box>
                        <Button
                            style={{ float: 'right', marginRight: 12 }}
                            variant="contained"
                            color="primary"
                            data-testid="add-device-profile"
                            onClick={(): void => navigateToAddDeviceProfile()}
                        >
                            <Add fontSize="medium" style={{ paddingRight: 2 }} />
                            {t('MANAGEDEVICEPROFILES.ADD_DERIVED_PROFILE')}
                        </Button>
                    </Box>

                    {isDataLoaded(deviceMasterData.deviceInfo) && deviceProfileGrp.length > 0 && (
                        <Box sx={{ marginTop: 5 }}>
                            <ChildProfileDetailsProvider orgId={orgId} deviceprofileId={selectedDeviceProfile || ''}>
                                <DeviceTypeListGroups
                                    groups={deviceProfileGrp}
                                    component={DeviceProfileListItem}
                                    componentTestId="manage-device-profile-item"
                                />
                            </ChildProfileDetailsProvider>
                        </Box>
                    )}
                    {!isDataLoaded(deviceMasterData.deviceInfo) && (
                        <DataIndicators
                            errorMessage="Device Profiles not found"
                            emptyMessage="Device Profiles not found"
                            inline
                            data={deviceMasterData.deviceInfo}
                            emptyIcon="Devices"
                            isEmpty={false}
                            isDetailedInfo={false}
                        />
                    )}
                </ContentContainer>
            </DeviceMasterProvider>
        </>
    );
};
